/**
 * Application header component.
 *
 * Fixed top utility bar with quick-create, module switcher, and user menu.
 * Module icon and title are displayed in the secondary nav panel instead.
 * On mobile, the module icon and title are shown in the header (no secondary nav).
 * Shows acting-as indicator when user is acting as a virtual user.
 * See docs/ai-agents.md for details.
 */

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Menu, X, Settings, RefreshCw, Search, Plus, MoreVertical } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '../ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { UserMenu } from './UserMenu';
import { QuickCreateMenu } from './QuickCreateMenu';
import { ModuleSwitcher } from './ModuleSwitcher';
import { GlobalAIButton } from '../../shared/components/chat/GlobalAIButton';
import { useActingAs } from '../../shared/contexts/ActingAsContext';
import { useModule } from '../../shared/contexts/ModuleContext';
import { useModuleName } from '../../shared/hooks/useModuleName';
import { useThemeMode } from '../../shared/contexts/ThemeContext';
import { OfflineIndicator } from '../../shared/offline';
import { getSecondaryPanelBg, MODULE_ACCENT_COLOR } from '../../shared/utils/color';
import { isCapacitor } from '../../shared/api/config';
import { AppBarNotificationBadges } from './AppBarNotificationBadges';
import { AppBarMailFilesShortcuts } from './AppBarMailFilesShortcuts';
import { useOverflowMenuUnread } from './useOverflowMenuUnread';
import { MessagingInboxPopover } from '../../modules/messaging/components/MessagingInboxPopover';
import { AnnouncementsBell } from '../../modules/software_factory/components/delivery/AnnouncementsBell';
import { CompanyScopeSwitcher } from '../../shared/components/multi-company';
import { InlineGlobalSearch } from '../../shared/components/global-search/InlineGlobalSearch';
import React from 'react';
import { cn } from '../../lib/utils';
import { HEADER_HEIGHT, HEADER_HEIGHT_CLASS } from '../../shared/styles/layout.constants';

interface HeaderProps {
  onMenuClick?: () => void;
  onSearchClick?: () => void;
  /** Controlled search open state for inline desktop search */
  searchOpen?: boolean;
  onSearchOpenChange?: (open: boolean) => void;
  showMenuButton?: boolean;
  isRtl?: boolean;
}

export function Header({ onMenuClick, onSearchClick, searchOpen = false, onSearchOpenChange, showMenuButton = false, isRtl: _isRtl = false }: HeaderProps) {
  const { t } = useTranslation(['common', 'aiAgents']);
  const getModuleName = useModuleName();
  const navigate = useNavigate();
  const { actingAsUser, isActingAs, stopActingAs } = useActingAs();
  const { currentModule } = useModule();
  const { resolvedTheme } = useThemeMode();
  const isDarkMode = resolvedTheme === 'dark';

  // Combined unread for the badged actions that fold into the mobile overflow
  // (⋮) menu — messaging, announcements, mail/files. Surfaced on the ⋮ trigger
  // so a folded-away unread is never hidden. Only rendered on mobile
  // (showMenuButton), where those items live in the menu.
  const overflowUnread = useOverflowMenuUnread();

  // Determine header background color - use pastel/light version of module color
  // Acting-as uses secondary color for visual distinction
  const headerBgColor = isActingAs ? 'hsl(var(--secondary))' : getSecondaryPanelBg(currentModule.color, isDarkMode);

  // Get module icon (only used on mobile)
  const ModuleIcon = currentModule.icon as React.ElementType;

  // Hard refresh (Capacitor only): clear all caches + unregister service
  // workers before reloading — a plain reload in the Android WebView serves
  // stale content from the HTTP cache. Shared between the desktop inline button
  // and the mobile overflow-menu entry.
  const handleHardRefresh = async () => {
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
    }
    if ('serviceWorker' in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((reg) => reg.unregister()));
    }
    window.location.reload();
  };

  return (
      <header
        className={cn(
          'z-40 flex items-center shrink-0',
          showMenuButton ? 'fixed inset-x-0 top-0 border-b px-2' : 'sticky top-0 border-b px-4',
          HEADER_HEIGHT_CLASS
        )}
        style={{
          backgroundColor: showMenuButton ? headerBgColor : undefined,
          // Mobile (Capacitor/notched browsers): span the full top so the
          // app-bar background covers the status-bar safe-area strip. Anchoring
          // at top:0 with padding-top = inset keeps the bar content in the same
          // visual position while preventing page content from peeking above
          // the bar during scroll/overscroll. Inline height overrides the h-12
          // class so the content box stays HEADER_HEIGHT tall under the inset.
          paddingTop: showMenuButton ? 'env(safe-area-inset-top, 0px)' : undefined,
          height: showMenuButton
            ? `calc(${HEADER_HEIGHT}px + env(safe-area-inset-top, 0px))`
            : undefined,
        }}
      >
        <div className="flex items-center justify-between w-full gap-2">
          {/* Left section — min-w-0 so the (variable-length) module title can
              truncate instead of pushing the pinned right cluster off-screen. */}
          <div className="flex items-center gap-1 sm:gap-2 min-w-0">
            {/* Mobile: hamburger menu */}
            {showMenuButton && (
              <Button
                variant="ghost"
                size="icon"
                onClick={onMenuClick}
                className="h-11 w-11 hover:bg-black/10"
                style={{ color: MODULE_ACCENT_COLOR }}
                aria-label={t('common:nav.openMenu')}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}

            {/* Desktop: module switcher moved to sidebar — no longer shown here */}

            {/* Mobile: module icon and title */}
            {showMenuButton && (
              <div className="flex items-center gap-2 min-w-0">
                <ModuleSwitcher
                  trigger={
                    <button className="flex items-center gap-2 h-10 px-1 rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors min-w-0">
                      <div
                        className="w-7 h-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ backgroundColor: currentModule.color }}
                      >
                        {typeof ModuleIcon === 'function' ? (
                          <ModuleIcon className="h-4 w-4 text-white" />
                        ) : (
                          <span className="[&>svg]:h-4 [&>svg]:w-4 text-white">
                            {currentModule.icon}
                          </span>
                        )}
                      </div>
                      <span className="font-semibold text-base truncate" style={{ color: MODULE_ACCENT_COLOR }}>
                        {getModuleName(currentModule)}
                      </span>
                    </button>
                  }
                  align="start"
                />
              </div>
            )}

            {/* Acting-as indicator */}
            {isActingAs && actingAsUser && (
              <Badge
                variant="secondary"
                className="bg-background text-foreground gap-1 px-2 sm:px-3 py-1.5 h-auto ml-2 max-w-[180px] sm:max-w-none"
              >
                <span className="text-xs sm:text-sm hidden sm:inline">{t('actingAs', 'Acting as')}:</span>
                <span dir="auto" className="text-xs sm:text-sm font-semibold truncate">{actingAsUser.full_name}</span>
                <button
                  type="button"
                  onClick={stopActingAs}
                  data-testid="stop-acting-as-button"
                  className="ml-1 rounded-full p-1 hover:bg-destructive/10 shrink-0 h-6 w-6 flex items-center justify-center"
                  aria-label={t('common:app.stopActingAs')}
                >
                  <X className="h-3.5 w-3.5 text-destructive" />
                </button>
              </Badge>
            )}

            {/* Global company scope (multi-company tenants, ERP collection
                surfaces only — self-hides everywhere else). Hidden on mobile,
                where the in-page company picker remains available and shares
                the same global scope. */}
            <CompanyScopeSwitcher className="hidden sm:flex" />
          </div>

          {/* Center section — inline search (desktop) or spacer (mobile) */}
          {!showMenuButton && onSearchOpenChange ? (
            <InlineGlobalSearch open={searchOpen} onOpenChange={onSearchOpenChange} />
          ) : (
            <div className="flex-1" />
          )}

          {/* Right section — min-w-0 so the cluster may shrink under extreme
              crowding, while the critical items (three-dots overflow menu, user
              menu, offline chip) are individually shrink-0 and therefore never
              compressed or clipped off-screen; the variable bells absorb the
              squeeze instead. Low-frequency actions fold into the three-dots
              menu on mobile (see below) so the inline set always fits at 375px. */}
          <div className="flex items-center gap-1 min-w-0" style={{ color: MODULE_ACCENT_COLOR }}>
            {/* Hard refresh — desktop inline only (Capacitor). On mobile it lives
                in the three-dots overflow menu. */}
            {isCapacitor && !showMenuButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleHardRefresh}
                    className="hover:bg-black/10"
                    style={{ color: MODULE_ACCENT_COLOR }}
                    aria-label={t('common:app.refresh')}
                  >
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>{t('common:app.refresh')}</TooltipContent>
              </Tooltip>
            )}
            {/* Desktop: Mail & Files quick-access shortcuts (conditional on
                feature enablement + Google/Microsoft integrations) */}
            {!showMenuButton && (
              <AppBarMailFilesShortcuts moduleColor={MODULE_ACCENT_COLOR} />
            )}
            {/* Notification badges. On mobile the messaging inbox and the
                announcements bell fold into the three-dots menu below (their
                unread rolls up onto the ⋮ trigger); only the system
                notifications Bell stays inline. */}
            <AppBarNotificationBadges
              moduleColor={MODULE_ACCENT_COLOR}
              includeAnnouncements={!showMenuButton}
              includeMessaging={!showMenuButton}
            />
            {/* Desktop: show AI, search, quick create inline */}
            {!showMenuButton && (
              <>
                <GlobalAIButton moduleColor={MODULE_ACCENT_COLOR} />
                <QuickCreateMenu />
              </>
            )}
            {/* Mobile: AI button + overflow menu */}
            {showMenuButton && (
              <GlobalAIButton moduleColor={MODULE_ACCENT_COLOR} />
            )}
            {showMenuButton && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="relative h-11 w-11 shrink-0 hover:bg-black/10"
                    style={{ color: MODULE_ACCENT_COLOR }}
                    aria-label={t('common:nav.openMenu')}
                  >
                    <MoreVertical className="h-5 w-5" />
                    {overflowUnread > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                        {overflowUnread > 99 ? '99+' : overflowUnread}
                      </span>
                    )}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[180px] p-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full justify-start gap-2 font-normal text-foreground"
                    onClick={onSearchClick}
                  >
                    <Search className="h-4 w-4" />
                    {t('common:app.search')}
                  </Button>
                  {/* Messaging inbox — folded into the menu on mobile so the
                      chat icon can't crowd the module title. Navigates to
                      /messaging; unread rolls up onto the ⋮ trigger above. */}
                  <MessagingInboxPopover
                    moduleColor={MODULE_ACCENT_COLOR}
                    variant="menu"
                  />
                  {/* Mail & Files shortcuts (same conditional entries as desktop) */}
                  <AppBarMailFilesShortcuts
                    moduleColor={MODULE_ACCENT_COLOR}
                    variant="menu"
                  />
                  {/* Announcements — folded into the menu on mobile so the
                      always-visible bell can't crowd the user menu off-screen. */}
                  <AnnouncementsBell
                    moduleColor={MODULE_ACCENT_COLOR}
                    variant="menu"
                  />
                  <QuickCreateMenu
                    trigger={
                      <Button variant="ghost" size="sm" className="w-full justify-start gap-2 font-normal text-foreground">
                        <Plus className="h-4 w-4" />
                        {t('common:app.create')}
                      </Button>
                    }
                  />
                  {/* Acting-as agent settings — folded into the menu on mobile so
                      the gear never competes for app-bar width. */}
                  {isActingAs && actingAsUser && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 font-normal text-foreground"
                      onClick={() => navigate('/ai-agents/settings')}
                    >
                      <Settings className="h-4 w-4" />
                      {t('aiAgents:settings.title')}
                    </Button>
                  )}
                  {/* Hard refresh (Capacitor) — folded into the menu on mobile. */}
                  {isCapacitor && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="w-full justify-start gap-2 font-normal text-foreground"
                      onClick={handleHardRefresh}
                    >
                      <RefreshCw className="h-4 w-4" />
                      {t('common:app.refresh')}
                    </Button>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
            <OfflineIndicator compact={showMenuButton} />
            {/* Agent avatar (identity indicator) shown when acting as — desktop
                only: on mobile the acting-as name badge on the left already
                shows the identity, and the avatar would crowd the user menu.
                The settings gear is inline on desktop; on mobile it lives in
                the three-dots overflow menu above. */}
            {isActingAs && actingAsUser && !showMenuButton && (
              <>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => navigate('/ai-agents/settings')}
                      className="hover:bg-black/10"
                      style={{ color: MODULE_ACCENT_COLOR }}
                      aria-label={t('aiAgents:settings.title')}
                    >
                      <Settings className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('aiAgents:settings.title')}
                  </TooltipContent>
                </Tooltip>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Avatar
                      className="h-9 w-9 border-2"
                      style={{ borderColor: MODULE_ACCENT_COLOR }}
                    >
                      <AvatarImage
                        src={actingAsUser.avatar_preview_url}
                        alt={actingAsUser.full_name}
                      />
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {actingAsUser.first_name?.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                  </TooltipTrigger>
                  <TooltipContent>
                    {actingAsUser.full_name}
                  </TooltipContent>
                </Tooltip>
              </>
            )}
            <UserMenu />
          </div>
        </div>
      </header>
  );
}
